import PropTypes from 'prop-types';
import React from 'react';
// -*- mode: jsx;-*-
import { ProjectCardWithSibling } from './ProjectCardWithSibling.js';
import { SearchContext } from './SearchContext.js';
import { EssentialsListCard } from './EssentialsListCard.js';
import { EssentialsListPromo } from './EssentialsListPromo.js';
import { hasNonEssentialsFilters } from './searchRequestUtils.js';
import { getURLParam } from '../../require/adapters/queryParamsAdapter.js';
import { SchoolRelatedSection } from './SchoolRelatedSection.js';


/**
 * Given a big list and a small list, return a single list consisting of half of the big list, all of the small list,
 * and then the rest of the big list.
 */
const embed = (bigList, smallList) => {
    const midpoint = bigList.length / 2;
    const front = bigList.slice(0, Math.floor(midpoint));
    const back = bigList.slice(Math.floor(midpoint), bigList.length);
    return front.concat(smallList).concat(back);
};

const showLists = (essentialsData, isEssentialsListsEnabled) => {
    // The top-level essentialsData is an object; nested within it is an array of results
    return essentialsData && essentialsData.essentialsLists && essentialsData.essentialsLists.length > 0 &&
        isEssentialsListsEnabled;
};

// TODO This probably needs to be extracted into another component?
const renderLists = (essentialsData, hasNonEssentialsFilters) => {
    const Component = hasNonEssentialsFilters ? EssentialsListPromo : EssentialsListCard;

    return <div className={'essentials-section ' + (hasNonEssentialsFilters ? ' promo' : '')}>
        {!hasNonEssentialsFilters &&
            <div className="essentials-header">
                <h3>Add-on basics for a teacher nearby</h3>  <div><a className="text-discreet" href={baseURL + 'donors/search.html?essentialsListsOnly=true'}>View all requests</a></div>
            </div>
        }
        <div className="selected-essentials">
            {
                essentialsData.essentialsLists.map((essentialsList) =>
                    <Component
                        teacherName={essentialsList.teacherName}
                        teacherNamePossessive={essentialsList.teacherNamePossessive}
                        topItemName={essentialsList.topItem.name}
                        topItemImageUrl={essentialsList.topItem.imageUrl}
                        topItemPrice={Math.ceil(essentialsList.topItem.price)}
                        listLength={essentialsList.listLength}
                        state={essentialsList.state}
                        stateFullName={essentialsList.stateFullName}
                        city={essentialsList.city}
                        essentialsListUrl={essentialsList.essentialsListUrl}
                        isMiniVersion={false}
                        hideTeacherDetails={essentialsList.hideTeacherDetails}
                        teacherId={essentialsList.teacherId}
                        // Add this prefix in case of ID-collisions with the project-list
                        key={`essentials-list-${essentialsList.essentialsListId}`}
                    />,
                )
            }
            <a href={baseURL + 'donors/map.html'} className="looking-for-more">
                <h3>Give to local classroom</h3>
                <p className="subheader">Explore a map of requests from teachers in your area</p>
                <a className="button-secondary small">View map</a>
            </a>
        </div>
    </div>;
};

const isSchoolSpecified = (isSchoolLevelGivingEnabled, searchURL, breadcrumb) => {
    // If school-level-giving is enabled, and the user has specified a school, we want to use a different layout for
    // search results.
    return isSchoolLevelGivingEnabled && !!getURLParam(searchURL, 'school') && schoolInfoFromBreadcrumb(breadcrumb) !== null;
}

const splitBySchool = (projectList, schoolId) => {
    let atSchool = [];
    let expanded = [];

    // Note that these are already rendered project-cards at this point, so we have to check item.props.project.
    projectList.forEach((renderedProject) => {
        if (renderedProject.props.project.schoolId == schoolId) {
            expanded.push(renderedProject);
        } else {
            atSchool.push(renderedProject);
        }
    })

    return [atSchool, expanded];
}

const schoolInfoFromBreadcrumb = (breadcrumb) => {
    if (!breadcrumb) {
        return null;
    }
    for (let i = 0; i < breadcrumb.length; i++) {
        let curr = breadcrumb[i]
        if (curr[0] === 'school') {
            return {
                id: parseInt(curr[1], 10),
                name: curr[2],
            };
        }
    }

    return null;
}

/**
 * If the user specified a school, group everything by in-school-vs-out-of-school and add a standalone "give to this
 * school" widget as well.
 */
const handleSchoolSpecificLayout = (essentialsData, isEssentialsListsEnabled, searchURL, projectList, breadcrumb) => {
    const { id, name } = schoolInfoFromBreadcrumb(breadcrumb);
    const [atSchool, expanded] = splitBySchool(projectList, id);

    const expandedPlusEssentialsLists = handleStandardLayout(essentialsData, isEssentialsListsEnabled, searchURL, expanded);

    return (
        <div className="single-school">
            <SchoolRelatedSection schoolName={name} schoolId={id} projectList={atSchool} key={0} />
            {expandedPlusEssentialsLists}
        </div>
    );
}

const handleStandardLayout = (essentialsData, isEssentialsListsEnabled, searchURL, projectList) => {
    let combinedList;
    if (showLists(essentialsData, isEssentialsListsEnabled)) {
        const listComponents = renderLists(essentialsData, hasNonEssentialsFilters(searchURL));
        combinedList = embed(projectList, listComponents);
    } else {
        combinedList = projectList;
    }

    return combinedList;
}

const isEssentialsOnly = (searchURL) => {
    return searchURL && searchURL.includes("essentialsListsOnly=true");
}
/**
 * A list of project-cards (or equivalent). Business rules re mixing/splitting one type of search-result into/from the
 * results for another type of search-result go here.
 */
export let ProjectList = function (props) {
    const { alwaysShowFavoritesWidget, projects, handleRemoveFavorite, searchURL, breadcrumb } = props;
    const { isEssentialsListsEnabled, essentialsData, isSchoolLevelGivingEnabled } = React.useContext(SearchContext);
    const essentialsOnly = isEssentialsOnly(searchURL);

    const projectList = projects.map(function (project) {
        return <ProjectCardWithSibling {...props} project={project} key={project.id} handleRemoveFavorite={handleRemoveFavorite} alwaysShowFavoritesWidget={alwaysShowFavoritesWidget} />;
    });

    let combinedList;
    if (isSchoolSpecified(isSchoolLevelGivingEnabled, searchURL, breadcrumb)) {
        combinedList = handleSchoolSpecificLayout(essentialsData, isEssentialsListsEnabled, searchURL, projectList, breadcrumb);
    } else {
        combinedList = handleStandardLayout(essentialsData, isEssentialsListsEnabled, searchURL, projectList);
    }

    return (
        <div className={essentialsOnly ? 'essentials-only' : ''}>
            {essentialsOnly &&
                <div className="essentials-only-header">
                    <h1>Give basics <span className="grey">to classrooms ASAP</span></h1>
                    <p className="text-discreet"><a href="https://help.donorschoose.org/hc/en-us/articles/23848400589207-Classroom-Essentials-lists#h_01HZ5J30970Q9A00PMEQX5GFF3">Classroom Essentials lists</a> allow teachers to shop for classroom supplies from a short, curated list of basics that teachers frequently have to resupply on. Teachers can ship each individual item as soon as it's funded.</p>
                </div>
            }
            {combinedList}
        </div>
    );
};

ProjectList.propTypes = {
    projects: PropTypes.arrayOf(PropTypes.object).isRequired,
    essentialsData: PropTypes.object,

    searchURL: PropTypes.string,
    challengeId: PropTypes.string,
    donorGivingPages: PropTypes.array,
    showGiveWidget: PropTypes.bool,
    cart: PropTypes.object,
    mainMatch: PropTypes.object,
    handleRemoveFavorite: PropTypes.func,
    alwaysShowFavoritesWidget: PropTypes.bool,
    breadcrumb: PropTypes.array,
};

ProjectList = React.memo(ProjectList);
